import React, { useState, useEffect } from 'react';
import { Zap, TrendingUp, ArrowDownCircle, RefreshCw, DollarSign, Activity } from 'lucide-react';

const API = 'https://api.naijapredict.net';
const T = { bg2:'#0d1114', border:'rgba(255,255,255,.06)', muted:'#4a5a63', green:'#00c566', yellow:'#f5c842', red:'#ff4444' };

export default function LPPositions() {
  const [positions, setPositions] = useState([]);
  const [totalValue, setTotalValue] = useState('0');
  const [loading, setLoading]     = useState(true);
  const [withdrawing, setWithdrawing] = useState(null);

  useEffect(() => { fetchPositions(); }, []);

  async function fetchPositions() {
    setLoading(true);
    try {
      const token = localStorage.getItem('np_creator_token');
      const res = await fetch(API+'/api/creator/lp-positions', {
        headers: { Authorization: 'Bearer '+token }
      });
      const data = await res.json();
      setPositions(data.positions||[]);
      setTotalValue(data.totalValue||'0');
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function withdraw(marketId, lpTokens) {
    if (!window.confirm('Withdraw liquidity from market #'+marketId+'?')) return;
    setWithdrawing(marketId);
    try {
      const token = localStorage.getItem('np_creator_token');
      const res = await fetch(API+'/api/creator/lp-withdraw', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:'Bearer '+token },
        body: JSON.stringify({ marketId, lpTokens })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      alert('Withdrawn! TX: '+data.txHash.slice(0,20)+'...');
      fetchPositions();
    } catch(e) { alert('Error: '+e.message); }
    finally { setWithdrawing(null); }
  }

  const stateColor = {0:T.green, 1:T.yellow, 2:T.red};

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4,flexWrap:'wrap',gap:10}}>
        <h1 style={{fontSize:'clamp(20px,4vw,28px)',fontWeight:800}}>LP Positions</h1>
        <button onClick={fetchPositions} style={{display:'flex',alignItems:'center',gap:6,padding:'7px 14px',borderRadius:8,border:`1px solid ${T.border}`,background:'rgba(255,255,255,.04)',color:T.muted,cursor:'pointer',fontSize:12,fontFamily:'inherit'}}>
          <RefreshCw size={13}/> Refresh
        </button>
      </div>
      <p style={{fontSize:14,color:T.muted,marginBottom:20}}>Track and manage your liquidity positions across all markets</p>

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12,marginBottom:20}}>
        {[
          [DollarSign, 'Total LP Value',    '$'+parseFloat(totalValue).toFixed(4)+' USDC', T.green],
          [Activity,   'Active Positions',  loading?'...':positions.filter(p=>p.state===0).length, T.green],
          [Zap,        'Total Positions',   loading?'...':positions.length, T.muted],
        ].map(([Icon,label,val,color])=>(
          <div key={label} style={{background:T.bg2,border:`1px solid ${T.border}`,borderRadius:14,padding:16}}>
            <Icon size={16} color={color} style={{marginBottom:10}}/>
            <div style={{fontSize:22,fontWeight:800,color}}>{val}</div>
            <div style={{fontSize:12,color:T.muted,marginTop:3}}>{label}</div>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div style={{background:'rgba(0,197,102,.04)',border:'1px solid rgba(0,197,102,.12)',borderRadius:14,padding:16,marginBottom:20}}>
        <div style={{fontSize:13,fontWeight:700,color:T.green,marginBottom:8}}>How LP Works</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:10,fontSize:12,color:T.muted}}>
          {[
            ['2% fees','Earned on every trade in your pool'],
            ['Your share','Proportional to your LP tokens'],
            ['Withdraw anytime','While market is active'],
            ['Auto-compound','Fees accrue in real time'],
          ].map(([v,d])=>(
            <div key={v} style={{display:'flex',gap:10,alignItems:'flex-start'}}>
              <div style={{fontSize:14,fontWeight:800,color:T.green,flexShrink:0}}>{v}</div>
              <div>{d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Positions */}
      {loading ? (
        <div style={{textAlign:'center',padding:40,color:T.muted}}>Loading positions...</div>
      ) : !positions.length ? (
        <div style={{background:T.bg2,border:`1px solid ${T.border}`,borderRadius:16,padding:40,textAlign:'center',color:T.muted}}>
          <Zap size={28} style={{marginBottom:12,opacity:.4}}/>
          <div style={{fontSize:15,fontWeight:600,marginBottom:4}}>No LP positions yet</div>
          <div style={{fontSize:13}}>Add liquidity to markets to start earning 2% fees</div>
        </div>
      ) : positions.map(p => (
        <div key={p.marketId} style={{background:T.bg2,border:`1px solid ${T.border}`,borderRadius:14,padding:18,marginBottom:12}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10,gap:12}}>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:600,lineHeight:1.5,marginBottom:4}}>{p.question}</div>
              <span style={{fontSize:11,color:stateColor[p.state],fontWeight:600}}>{p.stateLabel}</span>
              <span style={{fontSize:11,color:T.muted,marginLeft:8}}>{p.category}</span>
            </div>
            <div style={{textAlign:'right',flexShrink:0}}>
              <div style={{fontSize:18,fontWeight:800,color:T.green}}>${p.totalValue}</div>
              <div style={{fontSize:11,color:T.muted}}>Total value</div>
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(110px,1fr))',gap:8,marginBottom:12}}>
            {[
              ['My Share',    p.myShare+'%'],
              ['Deposited',   '$'+parseFloat(p.lpDeposited).toFixed(4)],
              ['Current Value','$'+p.myValue],
              ['Fees Earned', '$'+p.myFees],
              ['Pool Size',   '$'+p.poolSize],
              ['Expires',     new Date(p.expiresAt).toLocaleDateString('en-GB',{day:'2-digit',month:'short'})],
            ].map(([k,v])=>(
              <div key={k} style={{background:'rgba(255,255,255,.03)',borderRadius:8,padding:'8px 10px'}}>
                <div style={{fontSize:10,color:T.muted,marginBottom:3}}>{k}</div>
                <div style={{fontSize:13,fontWeight:600,color:k==='Fees Earned'?T.yellow:k==='Current Value'?T.green:'#e8eaeb'}}>{v}</div>
              </div>
            ))}
          </div>

          {p.canWithdraw && (
            <button
              onClick={()=>withdraw(p.marketId, p.lpTokens)}
              disabled={withdrawing===p.marketId}
              style={{width:'100%',padding:'10px',borderRadius:10,border:`1px solid rgba(255,107,53,.3)`,background:'rgba(255,107,53,.08)',color:'#ff6b35',fontWeight:600,fontSize:13,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
              <ArrowDownCircle size={14}/>
              {withdrawing===p.marketId?'Withdrawing...':'Withdraw Liquidity + Fees'}
            </button>
          )}
          {!p.canWithdraw && (
            <div style={{fontSize:12,color:T.muted,textAlign:'center',padding:'8px',background:'rgba(255,255,255,.03)',borderRadius:8}}>
              {p.state===1?'Market resolved — liquidity already withdrawn':'Market cancelled'}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
